import React, { useEffect, useState } from "react";

import { useModalContext } from "../../context/ModalContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Button from "../FormComponents/Button";
import MultiRadio from "../FormComponents/MultiRadio";
import SingleSelectBox from "../FormComponents/SingleSelectBox";
import { getToolTipAttributes } from "../../utils/tooltip";
import MaterialIcon from "../BuilderComponents/MaterialIcon";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../services/axiosService";

const StagingCard = ({ appData, title, setAppData }) => {
  const { getUserConfirmationForDelete } = useModalContext();
  const [isVersionOpen, setIsVersionOpen] = useState("");
  const [isSaveApplicationDataOpen, setSaveApplicationDataOpen] = useState("");
  const [isUploadApplicationDataOpen, setUploadApplicationDataOpen] = useState("");

  const createAction = async (type, id) => {
    let url;
    if (type === "stage") {
      url = `/application/staging-infra`;
    } else if (type === "prod") {
      url = `/application/infra`;
    }
    const response = await axiosInstance.post(url, null, {
      headers: {
        "Zino-App-Id": id
      }
    });
    setAppData({
      ...appData,
      [type === "stage" ? "staging_status" : "status"]: "starting"
    });
    toastSuccess(response.message);
  };

  const createApplicationLink = async (type, id) => {
    try {
      await createAction(type, id);
    } catch (error) {
      handleApiError(error);
    }
  };

  const deployAction = async (type, id) => {
    let url;
    if (type === "stage") {
      url = `/application/deploy/staging-env`;
    } else if (type === "prod") {
      url = `/application/deploy/staging`;
    }
    try {
      const response = await axiosInstance.post(url, null, {
        headers: {
          "Zino-App-Id": id
        }
      });
      toastSuccess(response.message);
    } catch (error) {
      handleApiError(error);
      console.error("Error deploying application:", error);
    }
  };

  const SetAppVersion = ({ id, version, cluster, onSave }) => {
    const [appVersions, setAppVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [selectedCluster, setSelectedCluster] = useState(cluster);

    const fetchAppVersions = async () => {
      try {
        const { data } = await axiosInstance.get(`/application/version`, {
          headers: {
            "Zino-App-Id": id
          }
        });
        setAppVersions(data);
        getSelectedVersion(data);
      } catch (error) {
        handleApiError(error);
      }
    };

    const handleUpdateVersion = (obj) => {
      setSelectedVersion(obj.id);
    };
    const getSelectedVersion = (data) => {
      const versionObj = data?.find((appversion) => appversion?.version === version);
      setSelectedVersion(versionObj?.id);
    };
    useEffect(() => {
      fetchAppVersions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="builder_form">
        <SingleSelectBox
          selectedData={selectedVersion}
          options={appVersions}
          labelKey={["version"]}
          onChange={(obj) => handleUpdateVersion(obj)}
          label="Application version"
          placeholder="Select Version"
          isSearch={true}
        />
        <MultiRadio
          label="Select Cluster"
          options={[
            { id: "kube", name: "kube" },
            { id: "machine", name: "machine" }
          ]}
          selectedValue={selectedCluster}
          onChange={(e) => setSelectedCluster(e.target.value)}
        />
        <Button
          onClick={() =>
            onSave(
              selectedCluster,
              selectedVersion,
              appVersions?.find((appversion) => appversion?.id === selectedVersion).version
            )
          }
        >
          Set version
        </Button>
      </div>
    );
  };
  const ImportApplicationData = ({ cluster, onSave }) => {
    const [selectedVersion, setSelectedVersion] = useState("");
    const [isImporting, setIsImporting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // Hardcoded options for the SingleSelectBox
    const appImportsOptions = [
      { id: "test", name: "Test" },
      { id: "prod", name: "Production" }
    ];

    const handleImport = async () => {
      if (!selectedVersion) return;

      setIsImporting(true);
      setSuccessMessage(""); // Clear any previous message

      try {
        await onSave(cluster, selectedVersion);
        setSuccessMessage("Import successful!");
      } catch (error) {
        setSuccessMessage("Failed to import. Please try again.");
      } finally {
        setIsImporting(false);
      }
    };

    return (
      <div className="builder_form">
        <SingleSelectBox
          selectedData={selectedVersion}
          options={appImportsOptions}
          labelKey="name"
          valueKey="id"
          onChange={(selectedOption) => setSelectedVersion(selectedOption.id)}
          label="Import App"
          placeholder="Test or Prod"
          isSearch={true}
        />

        <Button onClick={handleImport} disabled={!selectedVersion || isImporting}>
          {isImporting ? "Importing, please wait..." : "Import"}
        </Button>

        {successMessage && <div>{successMessage}</div>}
      </div>
    );
  };

  const SaveApplicationDataComponent = ({ onSave }) => {
    return (
      <div className="builder_form">
        <Button onClick={() => onSave()}>Save Data</Button>
      </div>
    );
  };
  const updateApplicationVersion = async (cluster, version, versionName) => {
    if (!version) {
      toastError("Select a version");
    }
    let url;
    if (isVersionOpen === "prod") {
      url = `/application/version/${version}`;
    }
    if (isVersionOpen === "stage") {
      url = `/application/staging/version/${version}`;
    }
    const clusterPayload = { cluster: cluster };
    try {
      const { message } = await axiosInstance.put(url, clusterPayload, {
        headers: {
          "Zino-App-Id": appData.id
        }
      });

      setAppData((appData) => {
        return {
          ...appData,
          ...(isVersionOpen === "stage"
            ? { version: versionName, cluster: cluster }
            : { staging_version: versionName, staging_cluster: cluster })
        };
      });

      setIsVersionOpen("");
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const saveApplicationData = async (comment) => {
    try {
      const { message } = await axiosInstance.post(`/application/data/checkpoint`, { comment });

      setSaveApplicationDataOpen("");
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const uploadAppData = async (cluster, version) => {
    if (!version) {
      toastError("Select a version");
      return;
    }

    let url;
    if (version === "prod") {
      url = "/application/data/import/production";
    } else if (version === "test") {
      url = "/application/data/import/test";
    } else {
      toastError("Invalid version selected");
      return;
    }

    const clusterPayload = { cluster: cluster };

    try {
      const { message } = await axiosInstance.post(url, clusterPayload, {
        headers: {
          "Zino-App-Id": appData.id
        }
      });

      setAppData((appData) => {
        return {
          ...appData,
          ...(version === "test"
            ? { version: version, cluster: cluster }
            : { staging_version: version, staging_cluster: cluster })
        };
      });

      setUploadApplicationDataOpen("");
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const closeVersionModal = () => {
    setIsVersionOpen("");
  };
  const closeSaveDataModal = () => {
    setSaveApplicationDataOpen("");
  };
  const closeUploadApplicationData = () => {
    setUploadApplicationDataOpen("");
  };
  // stop app
  // Destroy actions
  const destroyAction = async (type, id, name) => {
    const userConfirmed = await getUserConfirmationForDelete(name, "", "stop");
    if (!userConfirmed) return;

    let url;
    if (type === "stage") {
      url = `/application/staging-infra`;
    } else if (type === "prod") {
      url = `/application/infra`;
    }
    const response = await axiosInstance.delete(url, {
      headers: {
        "Zino-App-Id": id
      }
    });
    setAppData({
      ...appData,
      [type === "stage" ? "staging_status" : "status"]: "stopping"
    });
    toastSuccess(response.message);
  };

  const destroyApplicationLink = async (type, id, name) => {
    try {
      await destroyAction(type, id, name);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="card setting_card">
      <div className="setting_card_header">
        {title && <div className="card-title setting_card_title">{title}</div>}
        <div className="setting_card_actions_container">
          <Button
            variant="secondary"
            size="small"
            className="card-button edit"
            onClick={() => setUploadApplicationDataOpen("stage")}
            {...getToolTipAttributes("Import App Data")}
          >
            <MaterialIcon name="system_update_alt" className="action font_size_large custom-icon" />
          </Button>
          <Button
            variant="secondary"
            size="small"
            className="card-button edit"
            onClick={() => setSaveApplicationDataOpen("stage")}
            {...getToolTipAttributes("Save App Data")}
          >
            <MaterialIcon name="save" className="action font_size_large custom-icon" />
          </Button>
          {appData.staging_status === "live" && (
            <Button
              variant="secondary"
              size="small"
              className="card-button openapp"
              onClick={() => window.open(appData.staging_url, "blank")}
              {...getToolTipAttributes("Open Link")}
            >
              <MaterialIcon name="link" className="action font_size_large custom-icon" />
            </Button>
          )}

          <Button
            variant="secondary"
            size="small"
            className="card-button edit"
            onClick={() => setIsVersionOpen("stage")}
            {...getToolTipAttributes("Software Version")}
          >
            <MaterialIcon name="edit_note" className="action font_size_large custom-icon" />
          </Button>
          <Button
            variant="primary"
            size="small"
            className="card-button"
            onClick={() => deployAction("stage", appData.id)}
            {...getToolTipAttributes("Deploy")}
          >
            <MaterialIcon name="rocket_launch" className="action font_size_large" />
          </Button>
        </div>
      </div>
      <div className="card-body pb_3 p_0">
        <div className="mb_2 font_weight_medium setting_card_info_label">
          Software Version:{" "}
          <span className="font_weight_semibold setting_card_info_value">{appData.staging_version || "N/A"}</span>
        </div>
        <div className="mb_2 font_weight_medium setting_card_info_label">
          Cluster:{" "}
          <span className="font_weight_semibold setting_card_info_value">{appData.staging_cluster || "N/A"}</span>
        </div>
        <div className="mb_0 font_weight_medium setting_card_info_label">
          Application Version: <span className="font_weight_semibold setting_card_info_value">latest</span>
        </div>
      </div>
      <div className="card-footer d_flex justify_between align_center border_top pt_3">
        <div className={`app_status ${appData?.staging_status?.toLowerCase()} setting_app_status_badge`}>
          {appData?.staging_status || "Unknown"}
        </div>
        {appData.staging_status === "stopped" && (
          <Button
            size="small"
            variant="secondary"
            onClick={() => createApplicationLink("stage", appData.id)}
            isLoading={appData.staging_status === "starting"}
          >
            Start
          </Button>
        )}
        {appData.staging_status === "live" && (
          <Button
            size="small"
            variant="secondary"
            onClick={() => destroyApplicationLink("stage", appData.id, appData.name)}
            isLoading={appData.staging_status === "starting"}
          >
            Stop
          </Button>
        )}
      </div>

      <BuilderModal isOpen={isVersionOpen} onClose={closeVersionModal} modalName="Set Software Version">
        {isVersionOpen === "stage" && (
          <SetAppVersion
            id={appData.id}
            version={appData?.staging_version}
            cluster={appData?.staging_cluster}
            onSave={updateApplicationVersion}
          />
        )}
        {/* {isVersionOpen === "prod" && (
          <SetAppVersion
            id={appData.id}
            version={appData?.version}
            cluster={appData?.cluster}
            onSave={updateApplicationVersion}
          />
        )} */}
      </BuilderModal>

      <BuilderModal isOpen={isSaveApplicationDataOpen} onClose={closeSaveDataModal} modalName="Save Application Data">
        {isSaveApplicationDataOpen === "stage" && (
          <SaveApplicationDataComponent id={appData.id} onSave={saveApplicationData} />
        )}
      </BuilderModal>

      <BuilderModal
        isOpen={isUploadApplicationDataOpen}
        onClose={closeUploadApplicationData}
        modalName="Import Application Data"
      >
        {isUploadApplicationDataOpen === "stage" && <ImportApplicationData id={appData.id} onSave={uploadAppData} />}
      </BuilderModal>
    </div>
  );
};

export default StagingCard;
